import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  MessageSquareWarning, 
  Users, 
  AlertTriangle, 
  ShieldCheck, 
  Mail, 
  Phone, 
  MapPin, 
  Lock, 
  CheckCircle, 
  Ban
} from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/grievance`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Grievance & Contact Policy | SEVAA Foundation";
const pageDescription =
  "Read SEVAA Foundation's grievance and contact policy for beneficiaries, donors, volunteers, partners and the public to raise concerns fairly.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Grievance Policy",
    "Contact Policy",
    "Complaint Resolution",
    "Beneficiary Concerns",
    "Donor Support",
    "NGO Accountability"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Grievance & Contact Policy",
      item: pageUrl,
    },
  ],
};


const GrievancePolicy = () => {
  // Data for "Who Can Raise a Grievance"
  const whoCanRaise = [
    "Beneficiaries",
    "Donors",
    "Volunteers",
    "Partners",
    "General public"
  ];

  // Data for "Types of Grievances"
  const grievanceTypes = [
    "Program-related concerns",
    "Donation or communication issues",
    "Ethical or conduct-related concerns",
    "Any misuse or misrepresentation"
  ];

  // Data for "Resolution Process"
  const resolutionProcess = [
    {
      text: "All grievances are reviewed confidentially",
      icon: <Lock className="w-5 h-5 text-teal-600" />
    },
    {
      text: "Appropriate action is taken after internal assessment",
      icon: <CheckCircle className="w-5 h-5 text-amber-500" />
    },
    {
      text: "No retaliation is permitted against anyone raising a genuine concern",
      icon: <Ban className="w-5 h-5 text-rose-500" />
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation grievance and contact policy" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation grievance and contact policy" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] bg-teal-900 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
          <div className="absolute -bottom-40 left-20 w-96 h-96 bg-teal-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-4xl mx-auto px-6 flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-teal-800 rounded-2xl border border-teal-700 flex items-center justify-center mb-6 shadow-xl"
          >
            <MessageSquareWarning className="w-8 h-8 text-orange-400" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight"
          >
            Grievance & Contact
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-teal-100 text-base md:text-lg max-w-2xl font-medium"
          >
            SEVAA is strictly committed to addressing concerns, complaints, or grievances fairly and promptly. We welcome all inquiries and community engagements.
          </motion.p>
        </div>
      </div>

      {/* 2. GRIEVANCE POLICY CONTENT SECTION */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        
        <div className="bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 p-8 md:p-12 mb-10">
          
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* Left Column: Who & What */}
            <div className="space-y-10">
              
              {/* Who Can Raise */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-600">
                    <Users className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Who Can Raise a Grievance?</h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  {whoCanRaise.map((person, index) => (
                    <span key={index} className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium text-slate-700">
                      {person}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Types of Grievances */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900">Types of Grievances</h2>
                </div>
                <ul className="space-y-3">
                  {grievanceTypes.map((type, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0"></span>
                      <span className="text-slate-600 font-medium">{type}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

            </div>

            {/* Right Column: Resolution Process */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-3xl p-8 border border-slate-100 h-full"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-black text-slate-900">Resolution Process</h2>
              </div>
              
              <div className="space-y-6">
                {resolutionProcess.map((step, index) => (
                  <div key={index} className="flex gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                    <div className="mt-0.5 flex-shrink-0">
                      {step.icon}
                    </div>
                    <p className="text-slate-700 font-medium text-sm leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>

        </div>

        {/* 3. CONTACT US SECTION */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#0a4d3c] rounded-[2rem] shadow-2xl p-8 md:p-12 text-white relative overflow-hidden"
        >
          {/* Decorative Background circles */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-800 rounded-full -mr-20 -mt-20 opacity-50 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-700 rounded-full -ml-20 -mb-20 opacity-50 blur-2xl"></div>

          <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
            
            {/* Contact Text */}
            <div>
              <span className="text-amber-400 font-bold text-xs uppercase tracking-widest mb-2 inline-block">
                Get In Touch
              </span>
              <h2 className="text-3xl md:text-4xl font-black mb-4">Contact Us</h2>
              <p className="text-teal-50/80 leading-relaxed font-medium mb-8">
                We welcome inquiries, collaborations, and community engagement. If you have a grievance or just want to support our cause, reach out to us.
              </p>
              
              <div className="inline-block bg-teal-800/50 border border-teal-700/50 backdrop-blur-sm px-6 py-4 rounded-2xl">
                <p className="text-xs font-bold text-teal-300 uppercase tracking-widest mb-1">Head Office</p>
                <p className="font-bold text-lg">SEVAA (Swadesh Sevaa Charitable Foundation)</p>
              </div>
            </div>

            {/* Contact Details Cards */}
            <div className="space-y-4">
              
              {/* Email */}
              <a href="mailto:info@sevaafoundation.com" className="flex items-center gap-5 p-5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-2xl transition-all group backdrop-blur-md">
                <div className="w-12 h-12 bg-white text-teal-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-teal-200 font-bold uppercase tracking-wider mb-0.5">Email Us</p>
                  <p className="font-bold text-lg tracking-wide">info@sevaafoundation.com</p>
                </div>
              </a>

              {/* Phone */}
              <a href="tel:+919910218596" className="flex items-center gap-5 p-5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-2xl transition-all group backdrop-blur-md">
                <div className="w-12 h-12 bg-white text-teal-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-teal-200 font-bold uppercase tracking-wider mb-0.5">Call Us</p>
                  <p className="font-bold text-lg tracking-wide">+91 99102 18596</p>
                </div>
              </a>

              {/* Address */}
              <div className="flex items-center gap-5 p-5 bg-white/10 border border-white/10 rounded-2xl backdrop-blur-md">
                <div className="w-12 h-12 bg-white text-teal-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs text-teal-200 font-bold uppercase tracking-wider mb-0.5">Location</p>
                  <p className="font-bold text-lg tracking-wide">Noida, Uttar Pradesh, India</p>
                </div>
              </div>

            </div>

          </div>
        </motion.div>

      </div>
      </div>
    </>
  );
};

export default GrievancePolicy;
